import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Resizer from 'react-image-file-resizer';
import crossIcon from '../assets/icons/cross.svg';
import viewIcon from '../assets/icons/view.svg';
import { ReactComponent as DownloadIcon } from '../assets/icons/download.svg';
import { api } from '../api';

const defaultProject = {
  title: '',
  projectType: '',
  artists: [],
  releaseDate: '',
  endDate: '',
  coverArt: '',
  budget: '',
  costs: '',
  isArchived: false,
  contract: '',
  contracts: [],

  // NEW FIELDS (defaults only for *create*)
  labelShare: 50,
  termYears: 15,
  syncRights: '',
  territory: 'Worldwide',
};

const costsOptions = [
  { label: 'Recoupable',                 color: '#9eeb68' },
  { label: 'Recoupable (-Side Artists)', color: 'orange' },
  { label: 'Non-Recoupable',             color: '#ff4c4c' },
];

const syncRightsOptions = [
  { label: 'None',        color: '#ff6b6b' },
  { label: 'Master Only', color: '#31576c' },
  { label: 'Partial',     color: '#583e72' },
  { label: 'One Stop',    color: '#2f6b4f' },
];

// --- date helpers ---
function toInputDateString(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addYearsPreserveMonthDay(date, years) {
  const y = date.getFullYear() + years;
  const m = date.getMonth();
  const d = date.getDate();
  const result = new Date(Date.UTC(y, m, d));
  if (result.getUTCMonth() !== m) {
    return new Date(Date.UTC(y, m + 1, 0));
  }
  return result;
}

// --- sanitize helpers (avoid breaking old data) ---
const asNumberOrStrip = (v) => (v === '' || v === undefined || v === null ? undefined : Number(v));
const stripEmpty = (obj) => {
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (v === '' || v === undefined) return;
    out[k] = v;
  });
  return out;
};
const shallowDiff = (next, prev) => {
  const diff = {};
  const keys = new Set([...Object.keys(next || {}), ...Object.keys(prev || {})]);
  keys.forEach((k) => {
    const a = next?.[k];
    const b = prev?.[k];
    // compare arrays shallowly
    if (Array.isArray(a) || Array.isArray(b)) {
      const sa = JSON.stringify(a ?? []);
      const sb = JSON.stringify(b ?? []);
      if (sa !== sb) diff[k] = a;
      return;
    }
    if (a !== b) diff[k] = a;
  });
  return diff;
};

const humanContractName = (storedName = '') => {
  if (!storedName) return '';
  const marker = '__';
  const idx = storedName.indexOf(marker);
  return idx >= 0 ? storedName.slice(idx + marker.length) : storedName;
};

/**
 * ProjectModal – shared modal used for both Create and Edit
 */
export default function ProjectModal({
  isOpen,
  onClose,
  onSave,     // parent should POST for create, PUT for edit
  onDelete,   // optional
  allArtists = [],
  allProjects = [],
  initialData = null, // if provided ⇒ edit mode
  onArtistsRefresh,   // 👈 NEW (optional)
}) {
  const navigate = useNavigate();
  const [newProject, setNewProject] = useState(defaultProject);
  const [originalProject, setOriginalProject] = useState(null); // for diff on edit
const [coverArtPreview, setCoverArtPreview] = useState('');
const [coverArtReplacing, setCoverArtReplacing] = useState(false);
const [contractsUploading, setContractsUploading] = useState(false);
const [contractInputKey, setContractInputKey] = useState(0);
  const [artistSearchTerm, setArtistSearchTerm] = useState('');
  const [artistSuggestionsOpen, setArtistSuggestionsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [artistsIndex, setArtistsIndex] = useState(allArtists);
useEffect(() => setArtistsIndex(allArtists), [allArtists]);
// inline “create artist” panel state
const [artistCreateOpen, setArtistCreateOpen] = useState(false);
const [artistCreateEmails, setArtistCreateEmails] = useState(['']);

  const isEdit = Boolean(initialData);
  const headerTitle = isEdit ? 'Edit Project' : 'Add New Project';

  // normalizes date strings coming from backend to yyyy-mm-dd (for inputs)
  const normalizeDateInput = (val) => {
    if (!val) return '';
    // accept Date object or ISO string
    const d = typeof val === 'string' ? new Date(val) : val;
    if (isNaN(new Date(d).getTime())) return '';
    return toInputDateString(new Date(d));
  };

  // initialise on open
  useEffect(() => {
    if (!isOpen) return;

    if (initialData) {
      // DO NOT introduce defaults that could overwrite old docs.
      // Use existing values; if missing, keep input controlled with ''.
      const normalized = {
        ...initialData,
        releaseDate: normalizeDateInput(initialData.releaseDate),
        endDate: normalizeDateInput(initialData.endDate),

        // new fields → only set if present; otherwise '' so we don't send on update unless changed
        labelShare: initialData.labelShare ?? '',
        termYears: initialData.termYears ?? '',
        syncRights: initialData.syncRights ?? '',
        territory: initialData.territory ?? '',
        budget: initialData.budget ?? '', // keep budget controlled
        contract: initialData.contract || '', // preserve contract filename
        contracts: Array.isArray(initialData.contracts) ? initialData.contracts : (initialData.contract ? [initialData.contract] : []),
      };
      setNewProject(normalized);
      setOriginalProject(normalized);
      setCoverArtPreview(initialData.coverArt || '');
      setCoverArtReplacing(false);
      setArtistCreateOpen(false);
setArtistCreateEmails(['']);
    } else {
      setNewProject({ ...defaultProject });
      setOriginalProject(null);
      setCoverArtPreview('');
      setCoverArtReplacing(false);
      setArtistCreateOpen(false);
setArtistCreateEmails(['']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialData]);

  // Auto-calc End Date whenever releaseDate or termYears change (only update field locally)
  useEffect(() => {
    const { releaseDate, termYears } = newProject;
    if (!releaseDate || termYears === '' || termYears == null) return;
    const start = new Date(`${releaseDate}T00:00:00Z`);
    if (isNaN(start.getTime())) return;
    const years = Number(termYears);
    if (Number.isNaN(years)) return;
    const end = addYearsPreserveMonthDay(start, years);
    const endStr = toInputDateString(end);
    setNewProject((prev) => (prev.endDate === endStr ? prev : { ...prev, endDate: endStr }));
  }, [newProject.releaseDate, newProject.termYears]);

  // handlers
  const handleProjectInputChange = (e) => {
    const { name, value } = e.target;
    setNewProject((prev) => ({ ...prev, [name]: value }));
  };

  const handleProjectNumberChange = (e) => {
    const { name, value } = e.target;
    const numeric = value === '' ? '' : Number(value);
    setNewProject((prev) => ({ ...prev, [name]: numeric }));
  };

  const handleProjectFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Resizer.imageFileResizer(
      file,
      600,
      600,
      'JPEG',
      80,
      0,
      (uri) => {
        setNewProject((prev) => ({ ...prev, coverArt: uri }));
        setCoverArtPreview(uri);
        setCoverArtReplacing(false);
      },
      'base64'
    );
  };

  const handleContractFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const projectId = initialData?._id || newProject._id;
    if (!projectId) {
      alert('Save the project first, then upload contracts.');
      setContractInputKey((k) => k + 1); // clear file input
      return;
    }
    uploadContractsNow({ projectId, files });
  };

  const uploadContractsNow = async ({ projectId, files }) => {
    if (!projectId || !Array.isArray(files) || !files.length) return;

    setContractsUploading(true);
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append('contracts', file));

      const res = await api(`/api/projects/${projectId}/contract`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const error = await res.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(error.error || 'Failed to upload contracts');
      }
      const data = await res.json();

      // Merge server-returned stored filenames into local state
      setNewProject((prev) => {
        const existing = Array.isArray(prev.contracts) ? prev.contracts : (prev.contract ? [prev.contract] : []);
        const merged = [...existing];
        (Array.isArray(data.fileNames) ? data.fileNames : []).forEach((name) => {
          if (name && !merged.includes(name)) merged.push(name);
        });
        return { ...prev, contract: data.fileName || prev.contract, contracts: merged };
      });
      if (originalProject) {
        setOriginalProject((prev) => {
          const existing = Array.isArray(prev?.contracts) ? prev.contracts : (prev?.contract ? [prev.contract] : []);
          const merged = [...existing];
          (Array.isArray(data.fileNames) ? data.fileNames : []).forEach((name) => {
            if (name && !merged.includes(name)) merged.push(name);
          });
          return { ...prev, contract: data.fileName || prev?.contract, contracts: merged };
        });
      }

      setContractInputKey((k) => k + 1); // clear file input
      alert('Contracts uploaded ✅');
    } catch (err) {
      alert(err.message || 'Failed to upload contracts');
    } finally {
      setContractsUploading(false);
    }
  };

  const deleteContract = async (storedName) => {
    const projectId = initialData?._id || newProject._id;
    if (!projectId) return;
    const ok = window.confirm(`Delete contract "${humanContractName(storedName)}"?`);
    if (!ok) return;

    try {
      const res = await api(
        `/api/projects/${projectId}/contract?fileName=${encodeURIComponent(storedName)}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`Delete failed (${res.status}): ${t}`);
      }
      const data = await res.json();
      setNewProject((prev) => ({
        ...prev,
        contract: data.contract || '',
        contracts: Array.isArray(data.contracts) ? data.contracts : [],
      }));
      if (originalProject) {
        setOriginalProject((prev) => ({
          ...prev,
          contract: data.contract || '',
          contracts: Array.isArray(data.contracts) ? data.contracts : [],
        }));
      }
    } catch (err) {
      alert(err.message || 'Failed to delete contract');
    }
  };

  // inline-create artist email helpers
const addArtistEmailRow = () => setArtistCreateEmails(prev => [...prev, '']);
const removeArtistEmailRow = (idx) =>
  setArtistCreateEmails(prev => prev.filter((_, i) => i !== idx));
const updateArtistEmailRow = (idx, val) =>
  setArtistCreateEmails(prev => prev.map((e, i) => (i === idx ? val : e)));

const normalizeEmails = (list) => {
  const seen = new Set();
  const out = [];
  for (const e of list || []) {
    const v = String(e || '').trim();
    if (!v) continue;
    const key = v.toLowerCase();
    if (!seen.has(key)) { seen.add(key); out.push(v); }
  }
  return out;
};

  const projectArtists = Array.isArray(newProject.artists) ? newProject.artists : [];
  const filteredArtistList = (artistsIndex || []).filter(
  (artistObj) =>
    (artistObj?.name || '').toLowerCase().includes(artistSearchTerm.toLowerCase()) &&
    !projectArtists.includes(artistObj._id)
);

  const handleArtistSearch = (e) => {
    const val = e.target.value;
    setArtistSearchTerm(val);
    setArtistSuggestionsOpen(val.trim().length > 0);
  };

  const handleSelectArtist = (artistObj) => {
  setNewProject((prev) => ({
    ...prev,
    artists: [...(Array.isArray(prev.artists) ? prev.artists : []), artistObj._id],
  }));
  setArtistSearchTerm('');
  setArtistSuggestionsOpen(false);
  // close/reset inline-create UI
  setArtistCreateOpen(false);
  setArtistCreateEmails(['']);
};

  const handleRemoveArtist = (artistId) => {
    setNewProject((prev) => ({
      ...prev,
      artists: (Array.isArray(prev.artists) ? prev.artists : []).filter((id) => id !== artistId),
    }));
  };

// Create a new artist in DB (with emails), update list, select it, refresh parent, reset UI
const createNewArtist = async (name) => {
  const clean = String(name || '').trim();
  if (!clean) {
    alert('Please type a name first.');
    return;
  }
  const emails = normalizeEmails(artistCreateEmails);

  try {
    const res = await api('/api/artists', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: clean, emails }),
    });
    if (!res.ok) throw new Error(`Failed to create artist (${res.status})`);
    const newArtist = await res.json();

    // update local list for future searches
    setArtistsIndex((prev) => [newArtist, ...(prev || [])]);

    // select it immediately
    handleSelectArtist(newArtist);

    // ask parent to refresh global artists (so other modals/areas see it)
    if (typeof onArtistsRefresh === 'function') {
      try { await onArtistsRefresh(); } catch (_) {}
    }

    // clear UI
    setArtistSearchTerm('');
    setArtistSuggestionsOpen(false);
    setArtistCreateOpen(false);
    setArtistCreateEmails(['']);
  } catch (err) {
    alert(err.message || 'Failed to create artist');
  }
};

  // ---- SAVE (create vs edit) with safety for existing data ----
  const handleSaveClick = async () => {
    if (saving) return;
    if (!newProject.title?.trim()) {
      alert('Project title is required.');
      return;
    }

    setSaving(true);
    try {
      let projectId = null;

      if (!isEdit) {
        // CREATE: include defaults; strip empties; coerce numbers
        const payload = stripEmpty({
          title: newProject.title,
          projectType: newProject.projectType,
          artists: newProject.artists,
          releaseDate: newProject.releaseDate || null,
          endDate: newProject.endDate || null,
          coverArt: newProject.coverArt || '',
          budget: asNumberOrStrip(newProject.budget),
          costs: newProject.costs,

          // new fields
          labelShare: asNumberOrStrip(newProject.labelShare),
          termYears: asNumberOrStrip(newProject.termYears),
          syncRights: newProject.syncRights,
          territory: newProject.territory,
          isArchived: !!newProject.isArchived,
        });

        // Save project - parent will create it and refresh
        await onSave(payload);
      } else {
        // EDIT: send only changed fields; avoid overwriting old data accidentally
        const candidate = {
          title: newProject.title,
          projectType: newProject.projectType,
          artists: newProject.artists,
          releaseDate: newProject.releaseDate || null,
          endDate: newProject.endDate || null,
          coverArt: newProject.coverArt || '',
          budget: asNumberOrStrip(newProject.budget),
          costs: newProject.costs,

          // new fields
          labelShare: asNumberOrStrip(newProject.labelShare),
          termYears: asNumberOrStrip(newProject.termYears),
          syncRights: newProject.syncRights,
          territory: newProject.territory,
          isArchived: !!newProject.isArchived,
          contract: newProject.contract || '', // preserve contract field
          contracts: Array.isArray(newProject.contracts) ? newProject.contracts : [],
        };

        // compute diff against original; then strip empty so we don't send ""
        const diff = shallowDiff(candidate, originalProject || {});
        const payload = stripEmpty(diff);

        // If contract exists and wasn't changed, make sure it's preserved
        if (newProject.contract && !payload.contract) {
          payload.contract = newProject.contract;
        }

        // For existing projects, we have the ID - get it before saving
        projectId = initialData?._id || newProject._id;
        
        // Ensure we have the project ID
        if (!projectId) {
          console.error('Project ID not found for contract upload', { initialData, newProject });
        }

        // If diff ends up empty (no changes), still allow save to close modal gracefully
        await onSave(payload);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        overflowY: 'auto',
        padding: '24px',
        zIndex: 1000,
      }}
    >
      <div
        className="modal-content"
        style={{
          width: '600px',
          maxWidth: '90vw',
          backgroundColor: '#1e1e1e',
          color: 'white',
          borderRadius: '10px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          boxSizing: 'border-box',
          padding: '20px',
          maxHeight: 'calc(100vh - 48px)',
          overflowY: 'auto',
          margin: '0 auto',
        }}
      >
        <h2 style={{ margin: '0 0 10px 0', color: 'white' }}>{headerTitle}</h2>

        {/* Title */}
        <input
          type="text"
          name="title"
          placeholder="Project Title"
          value={newProject.title}
          onChange={handleProjectInputChange}
          disabled={saving}
          style={{
            width: '100%',
            padding: '10px',
            backgroundColor: '#333',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            fontFamily: 'inherit',
            boxSizing: 'border-box',
          }}
        />

        {/* Artists */}
        <label style={{ margin: '10px 0 5px 0' }}>Artists:</label>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder="Search artist by name..."
            value={artistSearchTerm}
            onChange={handleArtistSearch}
            onFocus={() => setArtistSuggestionsOpen(artistSearchTerm.trim().length > 0)}
            disabled={saving}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: '#333',
              color: 'white',
              border: 'none',
              fontFamily: 'inherit',
              borderRadius: '5px',
              boxSizing: 'border-box',
            }}
          />
          {artistSuggestionsOpen && artistSearchTerm.trim().length > 0 && !saving && (
  <div
    style={{
      position: 'absolute',
      top: '100%',
      left: '0',
      width: '100%',
      backgroundColor: '#1e1e1e',
      borderRadius: '5px',
      border: '1px solid #555',
      maxHeight: '220px',
      overflowY: 'auto',
      zIndex: 999,
      marginTop: '5px',
    }}
  >
    {filteredArtistList.length > 0 ? (
      filteredArtistList.map((artistObj) => (
        <div
          key={artistObj._id}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleSelectArtist(artistObj)}
          style={{
            padding: '10px',
            cursor: 'pointer',
            borderBottom: '1px solid #333',
          }}
        >
          {artistObj.name}
        </div>
      ))
    ) : (
      <div style={{ padding: '10px', color: '#999' }}>
        No matching artists
      </div>
    )}

    {!artistCreateOpen ? (
      <div
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setArtistCreateOpen(true)}
        style={{ padding: '10px', cursor: 'pointer', color: '#28a745', fontWeight: 600 }}
      >
        + Create “{artistSearchTerm}” with emails
      </div>
    ) : (
      <div style={{ padding: '10px', borderTop: '1px solid #333' }}>
        <div style={{ color: '#bbb', fontSize: 12, marginBottom: 6 }}>
          New artist: <strong style={{ color: '#fff' }}>{artistSearchTerm}</strong>
        </div>

        {/* emails list */}
        {artistCreateEmails.map((val, idx) => (
          <div key={idx} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
            <input
              type="email"
              placeholder="name@domain.com"
              value={val}
              onChange={(e) => updateArtistEmailRow(idx, e.target.value)}
              style={{
                flex: 1,
                padding: 8,
                background: '#2a2a2a',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
              }}
            />
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => removeArtistEmailRow(idx)}
              style={{
                background: '#333',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '6px 10px',
                cursor: 'pointer',
              }}
              title="Remove"
            >
              ✕
            </button>
          </div>
        ))}

        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={addArtistEmailRow}
          style={{
            padding: '6px 10px',
            background: '#555',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontWeight: 600,
            marginBottom: 8,
          }}
        >
          + Add email
        </button>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => createNewArtist(artistSearchTerm)}
            style={{
              padding: '6px 10px',
              background: '#28a745',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontWeight: 700,
            }}
            disabled={!artistSearchTerm.trim()}
          >
            Create & Add
          </button>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { setArtistCreateOpen(false); setArtistCreateEmails(['']); }}
            style={{
              padding: '6px 10px',
              background: '#555',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    )}
  </div>
)}
        </div>

        {/* selected artists chips */}
        {Array.isArray(projectArtists) && projectArtists.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {projectArtists.map((artistId) => {
              const found = (artistsIndex || []).find((a) => a._id === artistId);
              const artistName = found ? found.name : 'Unknown';
              return (
                <div
                  key={artistId}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: '#171717',
                    padding: '5px 8px',
                    borderRadius: '5px',
                    gap: '5px',
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  <span style={{ color: 'white' }}>{artistName}</span>
                  <img
                    src={crossIcon}
                    alt="Remove"
                    onClick={() => !saving && handleRemoveArtist(artistId)}
                    style={{
                      width: '15px',
                      height: '15px',
                      cursor: saving ? 'not-allowed' : 'pointer',
                      filter: 'brightness(0) saturate(100%) invert(20%)',
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* Label Share + Term row */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ margin: '0 0 5px 0', display: 'block' }}>Label Share (%)</label>
            <div style={{ position: 'relative' }}>
              <input
                type="number"
                name="labelShare"
                min={0}
                max={100}
                step={1}
                value={newProject.labelShare}
                onChange={handleProjectNumberChange}
                disabled={saving}
                placeholder="50"
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: '#333',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                  appearance: 'textfield',
                }}
              />
              <span
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#aaa',
                  pointerEvents: 'none',
                }}
              >
                %
              </span>
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <label style={{ margin: '0 0 5px 0', display: 'block' }}>Term (years)</label>
            <input
              type="number"
              name="termYears"
              min={0}
              step={1}
              value={newProject.termYears}
              onChange={handleProjectNumberChange}
              disabled={saving}
              placeholder="15"
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                appearance: 'textfield',
              }}
            />
          </div>
        </div>

        {/* Project Type */}
        <label style={{ margin: '10px 0 5px 0' }}>Project Type:</label>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {[
            { label: 'Album',        color: '#31576c' },
            { label: 'EP',           color: '#583e72' },
            { label: 'Single',       color: '#4a4a4a' },
            { label: 'Multi-Single', color: '#4a4a4a' },
          ].map(({ label, color }) => {
            const selected = newProject.projectType === label;
            return (
              <div
                key={label}
                onClick={() => !saving && setNewProject((prev) => ({ ...prev, projectType: label }))}
                style={{
                  padding: '5px 10px',
                  borderRadius: '5px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  backgroundColor: selected ? color : '#2c2c2c',
                  color: 'white',
                  fontWeight: selected ? 'bold' : 'normal',
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {label}
              </div>
            );
          })}
        </div>

        {/* Budget */}
        <label style={{ margin: '0 0 5px 0' }}>Budget (€):</label>
        <input
          type="text"
          name="budget"
          placeholder="e.g. 5000"
          value={newProject.budget}
          onChange={handleProjectInputChange}
          disabled={saving}
          style={{
            width: '100%',
            padding: '10px',
            backgroundColor: '#333',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            fontFamily: 'inherit',
            boxSizing: 'border-box',
          }}
        />

        {/* Costs */}
        <label style={{ margin: '0 0 5px 0' }}>Costs:</label>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {costsOptions.map((opt) => {
            const isSelected = newProject.costs === opt.label;
            return (
              <div
                key={opt.label}
                onClick={() => !saving && setNewProject((prev) => ({ ...prev, costs: opt.label }))}
                style={{
                  padding: '5px 8px',
                  borderRadius: '5px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  backgroundColor: isSelected ? opt.color : '#2c2c2c',
                  color: 'white',
                  fontWeight: 'bold',
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {opt.label}
              </div>
            );
          })}
        </div>

        {/* Sync Rights */}
        <label style={{ margin: '10px 0 5px 0' }}>Sync Rights:</label>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {syncRightsOptions.map((opt) => {
            const isSelected = newProject.syncRights === opt.label;
            return (
              <div
                key={opt.label}
                onClick={() => !saving && setNewProject((prev) => ({ ...prev, syncRights: opt.label }))}
                style={{
                  padding: '5px 8px',
                  borderRadius: '5px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  backgroundColor: isSelected ? opt.color : '#2c2c2c',
                  color: 'white',
                  fontWeight: 'bold',
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {opt.label}
              </div>
            );
          })}
        </div>

        {/* Territory */}
        <label style={{ margin: '10px 0 5px 0' }}>Territory:</label>
        <input
          type="text"
          name="territory"
          placeholder="Territory"
          value={newProject.territory}
          onChange={handleProjectInputChange}
          disabled={saving}
          style={{
            width: '100%',
            padding: '10px',
            backgroundColor: '#333',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            fontFamily: 'inherit',
            boxSizing: 'border-box',
          }}
        />

        {/* Release Date + End Date */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ margin: '0 0 5px 0', display: 'block' }}>Release Date:</label>
            <input
              type="date"
              name="releaseDate"
              value={newProject.releaseDate}
              onChange={handleProjectInputChange}
              disabled={saving}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ margin: '0 0 5px 0', display: 'block' }}>End Date:</label>
            <input
              type="date"
              name="endDate"
              value={newProject.endDate}
              onChange={handleProjectInputChange}
              disabled={saving}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        {/* Cover Art */}
        <label style={{ margin: '10px 0 5px 0' }}>Cover Art:</label>
        {(() => {
          const existingCoverArt = coverArtPreview || newProject.coverArt || '';
          const hasCoverArt = Boolean(existingCoverArt);
          return (
            <>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ width: 250 }}>
                  {hasCoverArt && !coverArtReplacing ? (
                    <span
                      style={{
                        display: 'inline-block',
                        width: '100%',
                        color: '#8bc34a',
                        background: '#1f2a1f',
                        border: 'none',
                        padding: 6,
                        borderRadius: 6,
                        fontSize: 12,
                        textAlign: 'center',
                        boxSizing: 'border-box',
                      }}
                    >
                      File uploaded
                    </span>
                  ) : (
                    <input
                      type="file"
                      name="coverArt"
                      accept="image/*"
                      onChange={handleProjectFileChange}
                      disabled={saving}
                      style={{
                        width: '100%',
                        padding: 6,
                        backgroundColor: '#2a2a2a',
                        color: 'white',
                        border: 'none',
                        borderRadius: 6,
                        fontFamily: 'inherit',
                        boxSizing: 'border-box',
                      }}
                    />
                  )}
                </div>

                {hasCoverArt && !coverArtReplacing && (
                  <button
                    type="button"
                    onClick={() => setCoverArtReplacing(true)}
                    disabled={saving}
                    style={{
                      padding: '6px 10px',
                      background: '#333',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      cursor: saving ? 'not-allowed' : 'pointer',
                      minWidth: 110,
                    }}
                  >
                    Replace
                  </button>
                )}

                {hasCoverArt && coverArtReplacing && (
                  <button
                    type="button"
                    onClick={() => {
                      setCoverArtReplacing(false);
                      const fallback = originalProject?.coverArt || '';
                      setNewProject((prev) => ({ ...prev, coverArt: fallback }));
                      setCoverArtPreview(fallback);
                    }}
                    disabled={saving}
                    style={{
                      padding: '6px 10px',
                      background: '#555',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      cursor: saving ? 'not-allowed' : 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                )}

                {hasCoverArt && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        const url = existingCoverArt;
                        if (!url) return;
                        if (url.startsWith('data:')) {
                          const win = window.open();
                          if (win) {
                            win.document.write(
                              `<html><body style="margin:0;background:#000;display:flex;align-items:center;justify-content:center;"><img src="${url}" alt="Cover Art" style="max-width:100%;max-height:100vh;"/></body></html>`
                            );
                          }
                        } else {
                          window.open(url, '_blank', 'noopener,noreferrer');
                        }
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        marginLeft: hasCoverArt && !coverArtReplacing ? 8 : 0,
                        marginRight: 4,
                        borderRadius: 6,
                        background: '#222',
                        border: '1px solid #2f2f2f',
                        cursor: 'pointer',
                        padding: 0,
                      }}
                      title="View cover art"
                    >
                      <img
                        src={viewIcon}
                        alt="View cover art"
                        style={{
                          width: 16,
                          height: 16,
                          filter:
                            'brightness(0) saturate(100%) invert(82%) sepia(0%) saturate(0%) hue-rotate(180deg) brightness(95%) contrast(90%)',
                        }}
                      />
                    </button>
                    <a
                      href={existingCoverArt}
                      download="cover-art.jpg"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        marginLeft: 4,
                        borderRadius: 6,
                        background: '#222',
                        border: '1px solid #2f2f2f',
                        cursor: 'pointer',
                        textDecoration: 'none',
                      }}
                      title="Download cover art"
                    >
                      <DownloadIcon style={{ width: 16, height: 16, fill: '#aaa' }} />
                    </a>
                  </>
                )}
              </div>

              {hasCoverArt && (
                <img
                  src={existingCoverArt}
                  alt="Cover Art Preview"
                  style={{
                    width: 80,
                    height: 80,
                    objectFit: 'cover',
                    borderRadius: 8,
                    marginTop: 8,
                    border: '1px solid #333',
                  }}
                />
              )}
            </>
          );
        })()}

        {/* Contract */}
        <label style={{ margin: '10px 0 5px 0' }}>Contract:</label>
        {(() => {
          const existingContracts = Array.isArray(newProject.contracts) && newProject.contracts.length
            ? newProject.contracts
            : (newProject.contract ? [newProject.contract] : []);
          const hasContract = existingContracts.length > 0;
          const projectId = initialData?._id || newProject._id;
          const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5001';

          return (
            <>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  width: '100%',
                  flexWrap: 'nowrap',
                }}
              >
                <input
                  key={contractInputKey}
                  type="file"
                  name="contracts"
                  onChange={handleContractFileChange}
                  disabled={saving || contractsUploading}
                  accept=".pdf,.doc,.docx"
                  multiple
                  style={{
                    width: 300,
                    minWidth: 300,
                    padding: 6,
                    background: '#2a2a2a',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                />

                <div
                  style={{
                    marginLeft: 'auto',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    flexShrink: 0,
                  }}
                >
                  {contractsUploading && (
                    <span
                      style={{
                        display: 'inline-block',
                        color: '#ddd',
                        background: '#333',
                        border: 'none',
                        padding: '6px 10px',
                        borderRadius: 6,
                        fontSize: 12,
                      }}
                    >
                      Uploading…
                    </span>
                  )}

                  {hasContract && (
                    <span
                      style={{
                        display: 'inline-block',
                        color: '#8bc34a',
                        background: '#1f2a1f',
                        border: 'none',
                        padding: '6px 10px',
                        borderRadius: 6,
                        fontSize: 12,
                      }}
                    >
                      {existingContracts.length} uploaded
                    </span>
                  )}
                </div>
              </div>

              {hasContract && (
                <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {existingContracts.map((name) => {
                    const href = `${BACKEND_URL}/uploads/${encodeURIComponent(name)}`;
                    return (
                      <div
                        key={name}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 8,
                          background: '#202020',
                          border: '1px solid #2d2d2d',
                          borderRadius: 6,
                          padding: '8px 10px',
                        }}
                      >
                        <span style={{ fontSize: 12, color: '#ddd', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {humanContractName(name)}
                        </span>
                        <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="View contract"
                            style={{
                              display: 'inline-flex',
                              width: 28,
                              height: 28,
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 6,
                              background: '#222',
                              border: '1px solid #333',
                            }}
                          >
                            <img
                              src={viewIcon}
                              alt="View contract"
                              style={{
                                width: 14,
                                height: 14,
                                filter: 'brightness(0) saturate(100%) invert(82%) sepia(0%) saturate(0%) hue-rotate(180deg) brightness(95%) contrast(90%)',
                              }}
                            />
                          </a>
                          <a
                            href={href}
                            download={humanContractName(name)}
                            title="Download contract"
                            style={{
                              display: 'inline-flex',
                              width: 28,
                              height: 28,
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 6,
                              background: '#222',
                              border: '1px solid #333',
                              textDecoration: 'none',
                            }}
                          >
                            <DownloadIcon style={{ width: 14, height: 14, fill: '#aaa' }} />
                          </a>
                          <button
                            type="button"
                            onClick={() => deleteContract(name)}
                            title="Delete contract"
                            style={{
                              display: 'inline-flex',
                              width: 28,
                              height: 28,
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 6,
                              background: '#2a1f1f',
                              border: '1px solid #3b2a2a',
                              cursor: 'pointer',
                              padding: 0,
                            }}
                          >
                            <img
                              src={crossIcon}
                              alt="Delete"
                              style={{
                                width: 12,
                                height: 12,
                                filter: 'brightness(0) saturate(100%) invert(65%) sepia(25%) saturate(2500%) hue-rotate(330deg) brightness(98%) contrast(95%)',
                              }}
                            />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          );
        })()}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '10px', alignItems: 'center' }}>
          <button
            onClick={handleSaveClick}
            disabled={saving}
            style={{
              padding: '10px 20px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              fontFamily: 'inherit',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              padding: '10px 20px',
              backgroundColor: '#555',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              fontFamily: 'inherit',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.8 : 1,
            }}
          >
            Cancel
          </button>

          {isEdit && typeof onDelete === 'function' && (
            <button
              onClick={onDelete}
              disabled={saving}
              style={{
                padding: '10px 20px',
                background: '#ea5a54',
                border: 'none',
                color: '#fff',
                borderRadius: 5,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.8 : 1,
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <span style={{ display: 'inline-block', fontWeight: 'bold' }}>×</span>
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}